import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Shield,
  Wrench,
  Clock,
  Users,
  Award,
  CheckCircle,
  Star
} from "lucide-react";
import { testimonials } from "../data/testimonials";
import ProductCategories from "../components/ProductCategories";
import FeaturedProducts from "../components/FeaturedProducts";
import { motion } from "framer-motion";
import Testimonials from "../components/TestmonialsScroller";

export default function HomePage() {
  const stats = [
    { icon: <Users size={24} />, number: "200+", label: "Happy Customers" },
    { icon: <Award size={24} />, number: "15+", label: "Years of Expertise" },
    { icon: <CheckCircle size={24} />, number: "300+", label: "Parts in Inventory" },
    { icon: <Clock size={24} />, number: "24/7", label: "Support Availability" },
  ];

  const CLOUDINARY_BASE_URL = process.env.REACT_APP_CLOUDINARY_IMG_BASE_URL || "";

  const getImageUrl = (path: string) => {
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${CLOUDINARY_BASE_URL}${path}`;
  };

  const features = [
    {
      icon: <Shield size={32} />,
      title: "Uncompromising Quality",
      description:
        "Every part is rigorously tested to meet OEM standards, ensuring your machinery runs efficiently and reliably.",
    },
    {
      icon: <Star size={32} />,
      title: "Expert Guidance",
      description:
        "Our seasoned professionals provide tailored recommendations, helping you choose the perfect component for your needs.",
    },
    {
      icon: <Clock size={32} />,
      title: "Fast & Reliable Delivery",
      description:
        "We ensure timely shipments and minimal downtime so your operations stay productive and uninterrupted.",
    },
  ];

  return (
    <section className="w-full overflow-x-hidden">
      {/* ================== SEO META TAGS ================== */}
      <Helmet>
        <title>
          Home | Asus by Parnyab Enterprises Pvt Ltd | Backhoe Loader Spare Parts Supplier in Delhi NCR
        </title>
        <meta
          name="description"
          content="Asus (Parnyab Enterprises Pvt Ltd) is a trusted supplier of premium backhoe loader spare parts in Delhi NCR. We offer OEM-quality filters, hydraulic kits, gears, lights, electrical components, and more for JCB, CAT, Tata Hitachi, Mahindra, ACE, Bull, L&T, Case, Terex, and Manitou. Get 24/7 support, expert guidance, and fast delivery for all construction equipment parts."
        />
        <meta
          name="keywords"
          content="
Asus, Parnyab Enterprises, Parnyab Enterprises Pvt Ltd, backhoe loader spare parts, backhoe loader spares, backhoe spare parts, backhoe spareparts, backhoe loader parts, backhoe loader sparepart, heavy machinery spares, heavy machinery parts, construction equipment spares, construction equipment parts, earthmoving equipment parts, loader spare parts, loader spares, loader parts, loader sparepart, loader spareparts, mini loader parts, mini loader spares, excavator parts, excavator spares, excavator spare parts, excavator spareparts, bulldozer parts, bulldozer spares, bulldozer spareparts, telehandler parts, telehandler spares, skid steer parts, skid steer spares, OEM filters, OEM filter kits, filters, hydraulic kits, hydraulic spares, hydraulic components, gears, gear parts, gear spares, electrical components, lights, machine components, loader components, backhoe components, spare parts supplier Delhi, spare parts supplier Delhi NCR, spare parts supplier Gurugram, spare parts supplier India, loader filters, backhoe filters, loader hydraulic kits, backhoe hydraulic kits, loader gear kits, backhoe gear kits, loader electricals, backhoe electricals, JCB spare parts, JCB spareparts, JCB sparepart, JCB loader parts, JCB loader spares, JCB backhoe parts, JCB backhoe spares, CAT spare parts, CAT spareparts, CAT sparepart, CAT loader parts, CAT loader spares, CAT backhoe parts, CAT backhoe spares, Tata Hitachi spare parts, Tata Hitachi spareparts, Tata Hitachi sparepart, Tata Hitachi loader parts, Tata Hitachi loader spares, Tata Hitachi backhoe parts, Tata Hitachi backhoe spares, Mahindra loader parts, Mahindra loader spares, Mahindra backhoe parts, Mahindra backhoe spares, Bull loader parts, Bull loader spares, Bull backhoe parts, Bull backhoe spares, ACE machinery spares, ACE loader parts, ACE loader spares, ACE backhoe parts, ACE backhoe spares, L&T backhoe parts, L&T loader parts, L&T spareparts, Case loader spares, Case loader parts, Case backhoe parts, Case spareparts, Terex backhoe spare parts, Terex loader parts, Terex spareparts, Manitou loader parts, Manitou loader spares, Manitou backhoe parts, Manitu loader parts, Manitu spare parts, loader parts Delhi, loader spares Delhi NCR, backhoe parts Delhi, heavy machinery spares Delhi, construction equipment parts Delhi NCR, earthmoving equipment parts India, OEM spare parts Delhi, OEM spare parts India, spare parts online Delhi, buy loader spare parts, buy backhoe spare parts, backhoe filters, loader filters, hydraulic kits India, hydraulic kits Delhi, gear kits India, gear kits Delhi, electrical spares India, electrical spares Delhi, lights spares Delhi, lights spares India
"
        />
        <link rel="canonical" href="https://parnyabindia.com/" />

        {/* Open Graph */}
        <meta property="og:title" content="Asus | Premium Backhoe Loader & Heavy Machinery Spare Parts" />
        <meta property="og:description" content="Asus supplies high-quality backhoe loader, excavator, bulldozer, mini loader, skid steer, and heavy machinery spare parts for JCB, CAT, Tata Hitachi, Mahindra, ACE, Bull, L&T, Case, Terex, and Manitou. OEM filters, hydraulic kits, gears, electricals, lights, and expert support in Delhi NCR." />
        <meta property="og:url" content="https://parnyabindia.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://parnyabindia.com/Asus_logo.png" />
        <meta property="og:site_name" content="Asus" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:image:alt" content="Asus Logo – Backhoe Loader & Heavy Machinery Spare Parts" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Asus | Backhoe Loader & Heavy Machinery Spare Parts Supplier in Delhi NCR" />
        <meta name="twitter:description" content="Premium JCB, CAT, Tata Hitachi, Mahindra, ACE, Bull, L&T, Case, Terex, and Manitou spare parts by Asus. OEM filters, hydraulic kits, gears, lights, electrical components with 24/7 expert support in Delhi NCR." />
        <meta name="twitter:image" content="https://parnyabindia.com/Asus_logo.png" />

        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Asus",
      "url": "https://parnyabindia.com",
      "logo": "https://parnyabindia.com/Asus_logo.png",
      "parentOrganization": {
        "@type": "Organization",
        "name": "Parnyab Enterprises Pvt Ltd"
      },
      "sameAs": [
        "${process.env.REACT_APP_ASUS_FB_LINK}",
        "${process.env.REACT_APP_ASUS_INSTAGRAM_LINK}",
        "${process.env.REACT_APP_ASUS_X_LINK}",
        "${process.env.REACT_APP_ASUS_LINKEDIN_LINK}"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-8295659245",
        "contactType": "Customer Service",
        "areaServed": "IN"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "West Rajiv Nagar, Sector-12",
        "addressLocality": "Gurugram",
        "addressRegion": "Haryana",
        "postalCode": "122001",
        "addressCountry": "IN"
      },
      "brandServed": ["JCB", "CAT", "Tata Hitachi", "Bull", "ACE", "Mahindra", "L&T", "Case", "Terex", "Manitou"]
    }
    `}
        </script>

        {/* Structured Data - Product */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Backhoe Loader & Heavy Machinery Spare Parts",
      "brand": "Asus",
      "category": "Construction Machinery Spare Parts",
      "description": "Premium OEM-quality spare parts including filters, hydraulic kits, gears, electricals, and lights for JCB, CAT, Tata Hitachi, Mahindra, ACE, Bull, L&T, Case, Terex, and Manitou in Delhi NCR.",
      "manufacturer": "Parnyab Enterprises Pvt Ltd",
      "offers": {
        "@type": "Offer",
        "url": "https://parnyabindia.com/",
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock"
      }
    }
    `}
        </script>

        {/* Structured Data - Service */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Heavy Machinery Spare Parts Supply",
      "provider": {
        "@type": "Organization",
        "name": "Asus"
      },
      "areaServed": {
        "@type": "Place",
        "name": "Delhi NCR, India"
      },
      "serviceType": "Backhoe loader, excavator, bulldozer, mini loader, skid steer, and heavy machinery spare parts supply",
      "description": "Supply of premium OEM-quality spare parts for construction equipment including filters, hydraulic kits, gears, electrical components, and lights for all major brands."
    }
    `}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section
        id="home"
        className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative max-w-8xl mx-auto px-6 sm:px-8 lg:px-8 py-6 sm:py-8 lg:py-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 50, rotate: -2 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-6 sm:space-y-8"
            >
              <div className="space-y-3 sm:space-y-4">
                <motion.h1
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 1, type: "spring", stiffness: 80 }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
                >
                  Premium{" "}
                  <span className="text-orange-500">Backhoe Loader</span> Spares
                </motion.h1>
                <motion.p
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 1 }}
                  className="text-base sm:text-lg text-gray-300 leading-relaxed"
                >
                  Your trusted partner for premium, high-performance spare parts that keep your machinery running at its best. From precision-engineered lights and electrical components to durable hydraulic seal kits and gear parts, we ensure reliability, efficiency, and minimal downtime for all your heavy machinery needs.
                </motion.p>
              </div>

              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  delay: 0.5,
                  duration: 0.7,
                  type: "spring",
                  stiffness: 100,
                }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/products"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-colors"
                >
                  <span>View Products</span>
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/contact-us"
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-colors text-center"
                >
                  Contact Us
                </Link>
              </motion.div>

              {/* Features */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
                {[
                  {
                    icon: <Shield className="text-amber-300" />,
                    title: "Premium Quality",
                    desc: "All parts rigorously tested for maximum reliability and durability.",
                    label: "Trusted",
                  },
                  {
                    icon: <Wrench className="text-cyan-300" />,
                    title: "Professional Support",
                    desc: "Expert guidance to help you choose the right parts for your machines.",
                    label: "24/7 Help",
                  },
                  {
                    icon: <Clock className="text-emerald-300" />,
                    title: "Quick Delivery",
                    desc: "Timely and secure shipping to keep your operations running smoothly.",
                    label: "Reliable",
                  },
                ].map((f, idx) => (
                  <motion.div
                    key={idx}
                    className="flex flex-col items-center text-center bg-gradient-to-tr from-slate-700 via-slate-600 to-slate-500/80 p-6 rounded-2xl shadow-2xl hover:scale-105 transform transition-transform duration-300 h-full relative overflow-hidden"
                    initial={{ opacity: 0, y: 30, rotateY: 15 }}
                    whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.3 }}
                  >
                    <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml,%3Csvg width=20 height=20 viewBox=0 0 20 20 xmlns=http://www.w3.org/2000/svg%3E%3Ccircle cx=10 cy=10 r=1 fill=white/%3E%3C/svg%3E')] pointer-events-none rounded-2xl"></div>
                    {f.icon}
                    <h3 className="text-white font-semibold text-lg sm:text-xl mb-1 relative z-10">
                      {f.title}
                    </h3>
                    <p className="text-gray-300 text-sm sm:text-base mb-2 relative z-10">
                      {f.desc}
                    </p>
                    <span className="bg-white/10 text-white text-xs px-3 py-1 rounded-full relative z-10">
                      {f.label}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              className="relative flex justify-center lg:justify-end mt-8 lg:mt-0"
              initial={{ scale: 0.95 }}
              animate={{ scale: [0.95, 1.05, 0.95], rotate: [0, 1, -1, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="relative w-full max-w-lg sm:max-w-2xl lg:max-w-3xl">
                <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-3xl p-6 sm:p-8 backdrop-blur-sm border border-orange-500/20">
                  <img
                    decoding="async"
                    src={getImageUrl("Home-1_ctpjob.webp")}
                    alt="Backhoe Loader"
                    className="w-full h-80 sm:h-96 lg:h-[28rem] object-cover rounded-2xl"
                  />
                </div>

                <motion.div
                  className="absolute -top-3 sm:-top-5 -right-3 sm:-right-5 bg-orange-600 text-white p-2 sm:p-3 rounded-xl shadow-lg text-center"
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                >
                  <div className="text-base sm:text-xl font-bold">300+</div>
                  <div className="text-xs sm:text-sm">Parts Available</div>
                </motion.div>

                <motion.div
                  className="absolute -bottom-3 sm:-bottom-5 -left-3 sm:-left-5 bg-white text-slate-900 p-2 sm:p-3 rounded-xl shadow-lg text-center"
                  animate={{ y: [0, 6, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                >
                  <div className="text-base sm:text-xl font-bold">24/7</div>
                  <div className="text-xs sm:text-sm">Support</div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ProductCategories />
      <FeaturedProducts />

      {/* ABOUT & STATS SECTION */}
      <section className="bg-gradient-to-br from-slate-50 to-white py-12 sm:py-16 lg:py-20">
        <div className="w-full max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16 lg:mb-20 px-4 sm:px-6 lg:px-0">
            <motion.h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              About <span className="text-orange-600">Asus</span>
            </motion.h2>
            <motion.p
              className="text-orange-600 font-semibold text-lg sm:text-xl mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              Precisely Engineered, Optimum Performance
            </motion.p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16 text-center">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                className="flex flex-col items-center rounded-2xl p-4 sm:p-6 shadow-lg transition-transform duration-300 hover:scale-105"
                style={{
                  background: "linear-gradient(135deg, #0f172a, #b87333)",
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
              >
                <div className="bg-white text-orange-600 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-3 sm:mb-4 shadow-md">
                  {stat.icon}
                </div>
                <div className="text-xl sm:text-2xl font-extrabold text-white mb-1">
                  {stat.number}
                </div>
                <div className="text-gray-200 font-medium text-sm sm:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>

          {/* About Content & Features */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 sm:mb-16">
            <motion.div
              className="space-y-4 sm:space-y-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  Reliability & Performance You Can Trust
                </h3>

                <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4">
                  At <span className="font-semibold">Asus</span>, a brand of{" "}
                  <span className="font-semibold">
                    Parnyab Enterprises Pvt. Ltd.
                  </span>
                  , we supply premium spare parts for{" "}
                  Every component is precisely engineered to deliver optimum performance.
                </p>

                <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4">
                  Our solutions meet or exceed OEM standards, combining advanced engineering, durability, and expert support.
                </p>

                <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed">
                  Trusted by professionals across industries, <span className="font-semibold">Asus</span> extends equipment life, reduces operating costs, and provides peace of mind.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  {[
                    "Guaranteed OEM Quality",
                    "Extensive Parts Inventory",
                    "Expert Technical Support",
                    "Transparent & Competitive Pricing",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center space-x-3 bg-orange-50 border-l-4 border-orange-600 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow duration-300"
                    >
                      <CheckCircle className="text-orange-600" size={20} />
                      <span className="text-gray-800 text-sm sm:text-base font-medium">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* About Image */}
            <motion.div
              className="relative w-full mt-6 lg:mt-0"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={getImageUrl("home-3_zleyoj.webp")}
                alt="Heavy machinery warehouse"
                className="w-full h-64 sm:h-80 md:h-96 lg:h-[28rem] xl:h-[32rem] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-3 -left-3 bg-orange-600 text-white rounded-xl shadow-lg text-center font-semibold px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base lg:px-5 lg:py-2.5 lg:text-lg">
                Years of Excellence
              </div>
            </motion.div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 mb-12 sm:mb-16">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                className="bg-gradient-to-br from-orange-50 via-white to-orange-100 p-6 sm:p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-transform duration-300 hover:-translate-y-1 cursor-pointer border border-orange-200"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
              >
                <div className="text-orange-600 mb-4 text-5xl sm:text-6xl flex justify-center drop-shadow-md">
                  {feature.icon}
                </div>
                <h4 className="text-lg sm:text-2xl font-bold text-slate-900 mb-3 sm:mb-4 text-center">
                  {feature.title}
                </h4>
                <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed text-center">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials testimonies={testimonials} />
    </section>
  );
};
